// Loads Poppins + pairing-screen styles before the renderer module does (Vite dedupes).
import '@signage-player/renderer-ui/index.css';
import { setLocale, t, mountPairVeil, unmountPairVeil } from '@signage-player/renderer-ui';
import {
  getKnownInstances,
  getInstanceLabels,
  generateInstanceId,
  peekUrlInstanceId,
  setInstanceNote,
  removeKnownInstance,
  KnownInstance,
  KnownInstanceDisplay,
} from '@signage-player/device-browser';
import { consumePickerReturn, pushInstanceUrl } from './instanceHistory';

const COUNTDOWN_SECONDS = 5;
const TRASH_CONFIRM_MS = 3000;

const PENCIL_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/></svg>`;
const TRASH_SVG = `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`;

/**
 * Bare-URL visits with known instances get a 5s picker instead of silently
 * adopting the last-used identity; a deliberate return (browser Back, the
 * pairing screen's back button) skips the countdown. Resolves after the
 * chosen/new id (and display) has been pushed to the URL so
 * resolveInstanceId() picks it up.
 */
export async function showInstancePickerIfNeeded(): Promise<void> {
  const returned = consumePickerReturn();
  if (peekUrlInstanceId()) return;
  const instances = getKnownInstances();
  if (instances.length === 0) return;
  setLocale((navigator.language || 'en').split('-')[0]);

  const chosen = await showPicker(instances, !returned);
  pushInstanceUrl(chosen.id, chosen.displayId);
}

function shortId(id: string): string {
  return `${id.slice(0, 4)}…${id.slice(-4)}`;
}

function lastUsedLabel(ts: number): string {
  const mins = Math.round((Date.now() - ts) / 60_000);
  if (mins < 1) return t('picker_just_now');
  if (mins < 60) return t('picker_min_ago', { n: String(mins) });
  const hours = Math.round(mins / 60);
  if (hours < 48) return t('picker_h_ago', { n: String(hours) });
  return t('picker_d_ago', { n: String(Math.round(hours / 24)) });
}

type PickerChoice = { id: string; displayId: number | null };
type Entry = { el: HTMLElement; value: PickerChoice | 'new' };

function showPicker(instances: KnownInstance[], autoStart: boolean): Promise<PickerChoice> {
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.id = 'instance-picker';
    overlay.innerHTML = `
      <style>
        /* No justify-content: center — on an overflowing scroll container it clips
           the top beyond scroll reach; the box's auto margins center it safely. */
        #instance-picker { position: fixed; inset: 0; z-index: 10000; background: #080d16; color: #eee;
          font-family: 'Poppins', system-ui, sans-serif; display: flex; flex-direction: column; align-items: center;
          overflow-y: auto; }
        #instance-picker, #instance-picker * { cursor: default; }
        /* Hero veil (renderer-ui pairVeil.ts) under everything: fixed so it stays put
           while the rows scroll; the box is positioned to paint above it. */
        #instance-picker .pair-veil { position: fixed; }
        /* Fixed width: edit-mode trash buttons and the armed "Sure?" text must
           reflow inside the rows, not resize and re-center the whole box. */
        #instance-picker .box { width: 660px; max-width: 90vw; margin: auto; padding: 24px 0; position: relative; }
        #instance-picker .title-row { display: flex; justify-content: space-between; align-items: center; gap: 16px;
          margin: 0 0 20px; }
        #instance-picker h1 { font-size: 24px; font-weight: 600; margin: 0; }
        #instance-picker .edit-toggle { display: flex; align-items: center; justify-content: center; width: 38px;
          height: 38px; padding: 0; background: #0d1626; border: 1px solid #2c3a4d; border-radius: 8px; color: #e8e8e8;
          cursor: pointer; transition: border-color 0.12s ease, background 0.12s ease, color 0.12s ease; }
        #instance-picker .edit-toggle * { cursor: pointer; }
        #instance-picker .edit-toggle:hover, #instance-picker.editing .edit-toggle { border-color: #3989eb;
          background: #14203a; color: #5da7ff; }
        #instance-picker .row { padding: 14px 18px; border: 1px solid #2c3a4d; border-radius: 8px;
          margin-bottom: 10px; cursor: pointer; display: flex; justify-content: space-between; gap: 24px;
          background: #0d1626; transition: border-color 0.12s ease, background 0.12s ease; }
        #instance-picker .row:hover, #instance-picker .row.active { border-color: #3989eb; background: #14203a; }
        #instance-picker .row * { cursor: pointer; }
        #instance-picker .row .label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        #instance-picker .meta { color: #afafaf; font-size: 14px; align-self: center; white-space: nowrap; }
        #instance-picker .rename-input { font: inherit; color: inherit; background: transparent; border: 0;
          border-bottom: 1px solid #3989eb; outline: none; padding: 0; width: 100%; }
        #instance-picker .row-trash { display: none; align-items: center; justify-content: center; gap: 6px;
          align-self: center; padding: 6px 8px; background: none; border: 1px solid #2c3a4d; border-radius: 6px;
          color: #afafaf; font: inherit; font-size: 13px; white-space: nowrap;
          transition: border-color 0.12s ease, color 0.12s ease; }
        /* An empty flex item still costs a gap and skews the icon off-center. */
        #instance-picker .trash-text:empty { display: none; }
        #instance-picker.editing .row-trash { display: flex; }
        #instance-picker .row-trash:hover, #instance-picker .row-trash.confirm { border-color: #ff5c5c; color: #ff5c5c; }
        #instance-picker.editing .row, #instance-picker.editing .row * { cursor: default; }
        #instance-picker.editing .row:hover { border-color: #2c3a4d; background: #0d1626; }
        #instance-picker.editing .row .label, #instance-picker.editing .row .label * { cursor: pointer; }
        #instance-picker.editing .row .label .name { text-decoration: underline dotted; text-underline-offset: 3px; }
        #instance-picker.editing .row .label .rename-input { cursor: text; }
        #instance-picker.editing .row .row-trash, #instance-picker.editing .row .row-trash * { cursor: pointer; }
        #instance-picker.editing .row.create { opacity: 0.4; pointer-events: none; }
        #instance-picker .countdown-line { min-height: 24px; margin-top: 6px; color: #5da7ff; font-size: 15px; }
        #instance-picker .picker-footer { position: fixed; left: 0; right: 0; bottom: 0; display: flex;
          justify-content: space-between; align-items: flex-end; gap: 24px; padding: 0 43px 35px; }
        #instance-picker .picker-logo { width: 400px; }
        #instance-picker .picker-logo a { display: block; }
        #instance-picker .picker-logo a, #instance-picker .picker-logo a * { cursor: pointer; }
        #instance-picker .picker-logo svg { width: 100%; height: auto; display: block; }
        #instance-picker .picker-links { display: flex; gap: 16px; }
        #instance-picker .picker-links a, #instance-picker .picker-links a * { color: #e8e8e8; font-size: 15px;
          text-decoration: none; cursor: pointer; }
        #instance-picker .picker-links a { padding: 10px 18px; border: 1px solid #2c3a4d; border-radius: 8px;
          background: #0d1626;
          white-space: nowrap; transition: border-color 0.12s ease, background 0.12s ease, color 0.12s ease; }
        #instance-picker .picker-links a::after { content: ' ↗'; color: #afafaf; }
        #instance-picker .picker-links a:hover { border-color: #3989eb; background: #14203a; color: #5da7ff; }
        #instance-picker .picker-links a:hover::after { color: #5da7ff; }
        @media screen and (max-width: 950px) { #instance-picker .picker-logo { width: 240px; } }
        @media screen and (max-width: 640px) {
          #instance-picker .box { width: 92vw; }
          #instance-picker h1 { font-size: 20px; }
          #instance-picker .row { padding: 12px 14px; gap: 12px; }
          #instance-picker .meta { font-size: 12px; }
          #instance-picker .picker-links { width: 100%; }
          #instance-picker .picker-links a { flex: 1; text-align: center; padding: 10px 12px; }
        }
        /* Narrow OR short screens: fixed footer over scrollable content always
           overlaps mid-scroll — drop the logo, put the links into the flow. */
        @media screen and (max-width: 640px), screen and (max-height: 860px) {
          #instance-picker .picker-logo { display: none; }
          #instance-picker .picker-footer { position: static; justify-content: center; align-items: center;
            flex-wrap: wrap; padding: 0 16px 24px; }
        }
        /* The near-black wordmark needs recoloring only on the dark background. */
        @media (prefers-color-scheme: dark) {
          #instance-picker .picker-logo svg path[fill='#1B1A19'] { fill: #e8e8e8; }
        }
        /* Light scheme: same palette as the pairing screen (white bg, #181818 text,
           #c7c9cf borders, blue hover on transparent). */
        @media (prefers-color-scheme: light) {
          #instance-picker { background: white; color: #181818; }
          #instance-picker .row { border-color: #c7c9cf; background: #ffffff; }
          #instance-picker .row:hover, #instance-picker .row.active { border-color: #1f75de; background: #ffffff; }
          #instance-picker.editing .row:hover { border-color: #c7c9cf; }
          #instance-picker .edit-toggle { color: #181818; border-color: #c7c9cf; background: #ffffff; }
          #instance-picker .edit-toggle:hover, #instance-picker.editing .edit-toggle { border-color: #1f75de;
            background: #ffffff; color: #1f75de; }
          #instance-picker .row-trash { border-color: #c7c9cf; }
          #instance-picker .rename-input { border-bottom-color: #1f75de; }
          #instance-picker .countdown-line { color: #1f75de; }
          #instance-picker .row-trash:hover, #instance-picker .row-trash.confirm { border-color: #ff5c5c; color: #ff5c5c; }
          #instance-picker .picker-links a, #instance-picker .picker-links a * { color: #181818; }
          #instance-picker .picker-links a { border-color: #c7c9cf; background: #ffffff; }
          #instance-picker .picker-links a:hover { border-color: #1f75de; background: #ffffff; color: #1f75de; }
          #instance-picker .picker-links a:hover * { color: #1f75de; }
        }
      </style>
      <canvas class="pair-veil" data-veil-clear="center"></canvas>
      <div class="box">
        <div class="title-row">
          <h1></h1>
          <button type="button" class="edit-toggle">${PENCIL_SVG}</button>
        </div>
        <div class="rows"></div>
        <div class="countdown-line"></div>
      </div>
      <div class="picker-footer">
        <div class="picker-logo"></div>
        <div class="picker-links">
          <a href="http://app.bsgn.cc/" target="_blank" rel="noopener">Dashboard</a>
          <a href="https://www.bohemicasignage.com/" target="_blank" rel="noopener">bohemicasignage.com</a>
        </div>
      </div>
    `;

    // Reuse the pairing screen's logo; the dark-scheme CSS above recolors the
    // near-black wordmark via the path[fill] attribute selector.
    const logoSvg = document.querySelector('#device-id-container .logo svg');
    if (logoSvg) {
      const clone = logoSvg.cloneNode(true) as SVGElement;
      const link = document.createElement('a');
      link.href = 'https://www.bohemicasignage.com/';
      link.target = '_blank';
      link.rel = 'noopener';
      link.appendChild(clone);
      (overlay.querySelector('.picker-logo') as HTMLElement).appendChild(link);
    }

    const veil = overlay.querySelector('canvas.pair-veil') as HTMLCanvasElement;
    (overlay.querySelector('h1') as HTMLElement).textContent = t('picker_title');
    (overlay.querySelector('.picker-links a') as HTMLElement).textContent = t('dashboard_link');

    const rowsEl = overlay.querySelector('.rows') as HTMLElement;
    const countdownLine = overlay.querySelector('.countdown-line') as HTMLElement;
    const editToggle = overlay.querySelector('.edit-toggle') as HTMLButtonElement;
    editToggle.title = t('picker_edit');
    editToggle.setAttribute('aria-label', t('picker_edit'));
    editToggle.setAttribute('aria-pressed', 'false');

    const entries: Entry[] = [];
    const rows: { inst: KnownInstance; label: HTMLElement; display: KnownInstanceDisplay | null }[] = [];
    const editingLabels = new Set<HTMLElement>();
    let active = 0;
    let secondsLeft = autoStart ? COUNTDOWN_SECONDS : -1;
    let editing = false;
    let armedTrash: { btn: HTMLButtonElement; timer: number } | null = null;

    const render = () => {
      entries.forEach((e, i) => e.el.classList.toggle('active', !editing && i === active));
      // min-height on the line keeps the layout stable when the text empties.
      countdownLine.textContent = secondsLeft >= 0 ? t('picker_starting_in', { seconds: String(secondsLeft) }) : '';
    };

    const select = (value: PickerChoice | 'new') => {
      if (timer !== null) window.clearInterval(timer);
      window.removeEventListener('keydown', onKey, true);
      unmountPairVeil(veil);
      overlay.remove();
      resolve(value === 'new' ? { id: generateInstanceId(), displayId: null } : value);
    };

    const timer = autoStart
      ? window.setInterval(() => {
          secondsLeft--;
          if (secondsLeft <= 0) {
            select(entries[0].value);
            return;
          }
          render();
        }, 1000)
      : null;

    const cancelCountdown = () => {
      if (secondsLeft >= 0) {
        secondsLeft = -1;
        if (timer !== null) window.clearInterval(timer);
        render();
      }
    };

    const disarmTrash = () => {
      if (!armedTrash) return;
      window.clearTimeout(armedTrash.timer);
      armedTrash.btn.classList.remove('confirm');
      (armedTrash.btn.querySelector('.trash-text') as HTMLElement).textContent = '';
      armedTrash.btn.setAttribute('aria-label', t('picker_delete'));
      armedTrash = null;
    };

    const setEditing = (on: boolean) => {
      editing = on;
      overlay.classList.toggle('editing', on);
      editToggle.title = t(on ? 'picker_edit_done' : 'picker_edit');
      editToggle.setAttribute('aria-label', editToggle.title);
      editToggle.setAttribute('aria-pressed', String(on));
      // Blur commits any rename still open before rows are rebuilt.
      (rowsEl.querySelector('input.rename-input') as HTMLInputElement | null)?.blur();
      rebuildRows();
    };

    const labelsFor = (list: KnownInstance[]) => getInstanceLabels(list, t('picker_unnamed'));

    // Names come from the backend and notes from user input — build with
    // textContent, never innerHTML.
    const renderLabel = (inst: KnownInstance, label: HTMLElement, text: string, display: KnownInstanceDisplay | null) => {
      label.textContent = '';
      // Own span so the edit-mode rename underline doesn't hit the id meta.
      const name = document.createElement('span');
      name.className = 'name';
      name.textContent = text;
      const idMeta = document.createElement('span');
      idMeta.className = 'meta';
      idMeta.textContent = display
        ? ` [${display.id}]${display.name ? ` ${display.name}` : ''} · ${shortId(inst.id)}`
        : inst.note && inst.name
          ? ` ${inst.name} · ${shortId(inst.id)}`
          : ` ${shortId(inst.id)}`;
      label.append(name, idMeta);
    };

    // A rename or removal can renumber other duplicates — redraw every row
    // that isn't mid-edit.
    const refreshLabels = () => {
      const labels = labelsFor(instances);
      for (const { inst, label, display } of rows) {
        if (!editingLabels.has(label)) renderLabel(inst, label, labels.get(inst.id) ?? t('picker_unnamed'), display);
      }
    };

    const startRename = (inst: KnownInstance, label: HTMLElement) => {
      if (label.querySelector('input')) return;
      const input = document.createElement('input');
      input.className = 'rename-input';
      input.value = inst.note ?? '';
      // What the row falls back to once the note is cleared.
      input.placeholder = labelsFor(instances.map(i => (i === inst ? { ...i, note: null } : i))).get(inst.id) ?? '';
      editingLabels.add(label);
      label.textContent = '';
      label.appendChild(input);
      input.focus();
      input.select();
      let done = false;
      const finish = (commit: boolean) => {
        if (done) return;
        done = true;
        if (commit) {
          setInstanceNote(inst.id, input.value);
          // Re-read so a failed write shows the old value.
          inst.note = getKnownInstances().find(k => k.id === inst.id)?.note ?? null;
        }
        editingLabels.delete(label);
        refreshLabels();
      };
      input.addEventListener('blur', () => finish(true));
      input.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          if (e.isComposing) return;
          input.blur();
        } else if (e.key === 'Escape') finish(false);
      });
    };

    const removeRow = (inst: KnownInstance, el: HTMLElement) => {
      removeKnownInstance(inst.id);
      const li = instances.indexOf(inst);
      if (li !== -1) instances.splice(li, 1);
      const idx = entries.findIndex(en => en.el === el);
      if (idx !== -1) entries.splice(idx, 1);
      for (let i = rows.length - 1; i >= 0; i--) if (rows[i].inst === inst) rows.splice(i, 1);
      active = Math.min(active, entries.length - 1);
      el.remove();
      refreshLabels();
      render();
    };

    const buildTrash = (inst: KnownInstance, el: HTMLElement) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'row-trash';
      btn.setAttribute('aria-label', t('picker_delete'));
      btn.innerHTML = `${TRASH_SVG}<span class="trash-text"></span>`;
      btn.addEventListener('click', e => {
        e.stopPropagation();
        if (armedTrash?.btn === btn) {
          disarmTrash();
          removeRow(inst, el);
          return;
        }
        disarmTrash();
        btn.classList.add('confirm');
        (btn.querySelector('.trash-text') as HTMLElement).textContent = t('picker_delete_confirm');
        btn.setAttribute('aria-label', `${t('picker_delete')} — ${t('picker_delete_confirm')}`);
        armedTrash = { btn, timer: window.setTimeout(disarmTrash, TRASH_CONFIRM_MS) };
      });
      return btn;
    };

    const attachRowHandlers = (entry: Entry) => {
      entry.el.addEventListener('click', () => {
        if (!editing) select(entry.value);
      });
      entry.el.addEventListener('mouseenter', () => {
        cancelCountdown();
        if (editing) return;
        // Deletions splice entries, so resolve the index at event time.
        const idx = entries.indexOf(entry);
        if (idx === -1) return;
        active = idx;
        render();
      });
    };

    // Normal mode: multi-display devices expand to one row per display (pure
    // selection targets). Edit mode: one row per device (rename/trash).
    const buildRow = (inst: KnownInstance, display: KnownInstanceDisplay | null): Entry => {
      const el = document.createElement('div');
      el.className = 'row';
      const label = document.createElement('span');
      label.className = 'label';
      rows.push({ inst, label, display });
      if (editing) {
        label.setAttribute('title', t('picker_rename'));
        label.addEventListener('click', e => {
          e.stopPropagation();
          startRename(inst, label);
        });
      }
      const used = document.createElement('span');
      used.className = 'meta';
      used.textContent = t('picker_last_used', { when: lastUsedLabel(inst.lastUsedAt) });
      el.append(label, used);
      if (editing) el.appendChild(buildTrash(inst, el));
      const entry: Entry = { el, value: { id: inst.id, displayId: display?.id ?? null } };
      attachRowHandlers(entry);
      return entry;
    };

    const rebuildRows = () => {
      disarmTrash();
      rowsEl.textContent = '';
      entries.length = 0;
      rows.length = 0;
      editingLabels.clear();
      for (const inst of instances) {
        if (!editing && inst.displays.length >= 2) {
          for (const d of inst.displays) entries.push(buildRow(inst, d));
        } else {
          entries.push(buildRow(inst, null));
        }
      }
      refreshLabels();
      const createEl = document.createElement('div');
      createEl.className = 'row create';
      const createLabel = document.createElement('span');
      createLabel.className = 'label';
      createLabel.textContent = `＋ ${t('picker_create_new')}`;
      createEl.appendChild(createLabel);
      const createEntry: Entry = { el: createEl, value: 'new' };
      attachRowHandlers(createEntry);
      entries.push(createEntry);
      for (const e of entries) rowsEl.appendChild(e.el);
      active = 0;
      render();
    };

    rebuildRows();

    editToggle.addEventListener('click', () => {
      cancelCountdown();
      setEditing(!editing);
    });

    const onKey = (e: KeyboardEvent) => {
      cancelCountdown();
      if (editing) {
        // The open rename input handles its own Escape (cancel).
        if (e.key === 'Escape' && !(e.target instanceof HTMLInputElement)) {
          setEditing(false);
          e.preventDefault();
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        active = (active + 1) % entries.length;
        render();
        e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        active = (active - 1 + entries.length) % entries.length;
        render();
        e.preventDefault();
      } else if (e.key === 'Enter') {
        // Capture-phase listener: don't hijack Enter meant for a button/link.
        if (e.target instanceof HTMLElement && e.target.closest('button, a')) return;
        select(entries[active].value);
        e.preventDefault();
      }
    };

    overlay.addEventListener('pointerdown', e => {
      cancelCountdown();
      if (armedTrash && !armedTrash.btn.contains(e.target as Node)) disarmTrash();
    });
    window.addEventListener('keydown', onKey, true);

    document.body.appendChild(overlay);
    // Same options as renderer.ts (which loads only after the picker resolves);
    // the first caller pins the app-wide scheme + clear anchor.
    mountPairVeil(veil, { scheme: 'auto', clear: 'center' });
    render();
  });
}
